// Resume Service - CRUD operations for SavedResume collection
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import type { SavedResume } from '@shared/types';

const COLLECTION_NAME = 'savedResumes';

/**
 * Get resumes from existing applications (that have resumeUrl)
 */
export async function getApplicationResumes(userId: string): Promise<Array<{
  appId: string;
  company: string;
  jobTitle: string;
  resumeUrl: string;
  resumePath: string;
  appliedDate: any;
}>> {
  const db = getFirestore();
  const appsQuery = query(
    collection(db, 'applications'),
    where('userId', '==', userId),
  );
  const snapshot = await getDocs(appsQuery);
  const results: Array<{
    appId: string;
    company: string;
    jobTitle: string;
    resumeUrl: string;
    resumePath: string;
    appliedDate: any;
  }> = [];

  for (const d of snapshot.docs) {
    const data = d.data();
    if (data.resumeUrl && data.resumePath) {
      results.push({
        appId: d.id,
        company: data.company || '',
        jobTitle: data.jobTitle || '',
        resumeUrl: data.resumeUrl,
        resumePath: data.resumePath,
        appliedDate: data.appliedDate,
      });
    }
  }
  return results;
}

/**
 * Import a resume from an existing application into savedResumes
 * Checks for duplicates by storagePath before importing
 */
export async function importResumeFromApplication(
  userId: string,
  name: string,
  resumeUrl: string,
  storagePath: string,
): Promise<SavedResume> {
  const db = getFirestore();

  // Check if already imported (by storagePath)
  const existingQuery = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('storagePath', '==', storagePath)
  );
  const existingSnapshot = await getDocs(existingQuery);

  if (!existingSnapshot.empty) {
    // Already exists, return the existing one
    const existingDoc = existingSnapshot.docs[0];
    const data = existingDoc.data();
    return {
      id: existingDoc.id,
      userId: data.userId,
      name: data.name,
      fileName: data.fileName,
      storagePath: data.storagePath,
      downloadUrl: data.downloadUrl,
      size: data.size,
      skills: data.skills || [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as SavedResume;
  }

  const resumeData = {
    userId,
    name,
    fileName: storagePath.split('/').pop() || 'resume.pdf',
    storagePath,
    downloadUrl: resumeUrl,
    size: 0,
    skills: [] as string[],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), resumeData);
  return { id: docRef.id, ...resumeData } as SavedResume;
}

/**
 * Get all saved resumes for a user
 */
export async function getSavedResumes(userId: string): Promise<SavedResume[]> {
  const db = getFirestore();
  const resumesQuery = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(resumesQuery);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      name: data.name,
      fileName: data.fileName,
      storagePath: data.storagePath,
      downloadUrl: data.downloadUrl,
      size: data.size,
      skills: data.skills || [],
      embedding: data.embedding,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as SavedResume;
  });
}

/**
 * Upload and create a new saved resume.
 * Note: Skills and embedding will be populated by the server-side process_resume function.
 */
export async function createSavedResume(
  userId: string,
  file: File,
  name: string
): Promise<SavedResume> {
  const storage = getStorage();
  const db = getFirestore();

  // Create unique storage path
  const timestamp = Date.now();
  const storagePath = `users/${userId}/resumes/${timestamp}_${file.name}`;
  const storageRef = ref(storage, storagePath);

  // Upload file
  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);

  // Create Firestore document (skills/embedding will be added by server-side function)
  const resumeData = {
    userId,
    name,
    fileName: file.name,
    storagePath,
    downloadUrl,
    size: file.size,
    skills: [] as string[],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), resumeData);

  return {
    id: docRef.id,
    ...resumeData,
  } as SavedResume;
}

/**
 * Update a saved resume's metadata (name, skills, embedding)
 */
export async function updateSavedResume(
  resumeId: string,
  updates: Partial<Pick<SavedResume, 'name' | 'skills' | 'embedding'>>
): Promise<void> {
  const db = getFirestore();
  const docRef = doc(db, COLLECTION_NAME, resumeId);

  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a saved resume (both storage file and Firestore document)
 */
export async function deleteSavedResume(resumeId: string, storagePath: string): Promise<void> {
  const storage = getStorage();
  const db = getFirestore();

  // Delete from storage
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file from storage:', error);
    // Continue to delete document even if storage deletion fails
  }

  // Delete Firestore document
  const docRef = doc(db, COLLECTION_NAME, resumeId);
  await deleteDoc(docRef);
}

/**
 * Update resume skills and optionally embedding
 */
export async function updateResumeSkillsAndEmbedding(
  resumeId: string,
  skills: string[],
  embedding?: number[]
): Promise<void> {
  const db = getFirestore();
  const docRef = doc(db, COLLECTION_NAME, resumeId);

  const updates: Record<string, unknown> = {
    skills,
    updatedAt: Timestamp.now(),
  };

  if (embedding) {
    updates.embedding = embedding;
  }

  await updateDoc(docRef, updates);
}
